import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  SignIn, SignUp, Restore,
  Welcome, ResumeBuilder, ResumeReview, AITools, Autofill, Extension,
  Profile, CRM, Upgrade, Dashboard, Contacts, Documents, NewBaseResume, ResumeEditor, Purchase, PaymentSuccess, EmojisDebug, NotFound
} from './index';
import { ConfirmEmail } from './pages/(logging)/confirm-email';
import Banned from './pages/(logging)/banned';
import AdminOverview from './pages/(logged-in)/admin/AdminOverview';
import AdminUsers from './pages/(logged-in)/admin/AdminUsers';
import AdminUserDetail from './pages/(logged-in)/admin/AdminUserDetail';
import AdminAIUsage from './pages/(logged-in)/admin/AdminAIUsage';
import AdminPlans from './pages/(logged-in)/admin/AdminPlans';
import AdminBlog from './pages/(logged-in)/admin/AdminBlog';
import AdminBlogEditor from './pages/(logged-in)/admin/AdminBlogEditor';
import AdminAISettings from './pages/(logged-in)/admin/AdminAISettings';
import ImpersonationBanner from './components/organisms/ImpersonationBanner';
import AdminRoute from './components/organisms/AdminRoute';
import ProtectedRoute from './components/organisms/ProtectedRoute';
import UpsellModal from './components/organisms/UpsellModal';
import OnboardingChecklistWidget from './components/organisms/OnboardingChecklistWidget';
import CookieConsent from './components/organisms/CookieConsent';
import { UserProvider } from './context/UserContext';
import { ToastContainer } from './components/organisms/ToastContainer';
import { trackPageView, track } from './utils/analytics';
import './App.css';

const PageTracker: React.FC = () => {
  const location = useLocation();
  const entryTimeRef = useRef<number>(Date.now());
  const prevPathRef = useRef<string>('');

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const now = Date.now();

    if (prevPath) {
      const seconds = Math.round((now - entryTimeRef.current) / 1000);
      if (seconds >= 2) {
        track('time_on_page', { page: prevPath, seconds });
      }
    }

    trackPageView(location.pathname);
    entryTimeRef.current = now;
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <UserProvider>
      <Router>
        <PageTracker />
        <UpsellModal />
        <OnboardingChecklistWidget />
        <CookieConsent />
        <div className="App">
          <ImpersonationBanner />
          <ToastContainer />
          <Routes>
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/restore" element={<Restore />} />
          <Route path="/confirm/email/:token" element={<ConfirmEmail />} />
          
          <Route path="/" element={<Navigate to="/welcome" replace />} />
          <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
          <Route path="/resume-builder" element={<ProtectedRoute><ResumeBuilder /></ProtectedRoute>} />
          <Route path="/resume-builder/new-base-resume" element={<ProtectedRoute><NewBaseResume /></ProtectedRoute>} />
          <Route path="/resume-builder/editor/:uid" element={<ProtectedRoute><ResumeEditor /></ProtectedRoute>} />
          <Route path="/resume-review" element={<ProtectedRoute><ResumeReview /></ProtectedRoute>} />
          <Route path="/ai-tools" element={<ProtectedRoute><AITools /></ProtectedRoute>} />
          <Route path="/autofill" element={<ProtectedRoute><Autofill /></ProtectedRoute>} />
          <Route path="/extension" element={<ProtectedRoute><Extension /></ProtectedRoute>} />

          <Route path="/profile" element={<Navigate to="/profile/builder/basic-info" replace />} />
          <Route path="/profile/builder" element={<Navigate to="/profile/builder/basic-info" replace />} />
          <Route path="/profile/builder/:tab" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />

          <Route path="/track/board/:boardId" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
          <Route path="/track/board/:boardId/job/:jobId" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
          <Route path="/settings/plan/purchase" element={<ProtectedRoute><Purchase /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

          <Route path="/debug/emojis" element={<ProtectedRoute><EmojisDebug /></ProtectedRoute>} />
          
          <Route path="/admin" element={<AdminRoute><AdminOverview /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/users/:uid" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
          <Route path="/admin/ai-usage" element={<AdminRoute><AdminAIUsage /></AdminRoute>} />
          <Route path="/admin/ai-settings" element={<AdminRoute><AdminAISettings /></AdminRoute>} />
          <Route path="/admin/plans" element={<AdminRoute><AdminPlans /></AdminRoute>} />
          <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
          <Route path="/admin/blog/:uid" element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />

          <Route path="/banned" element={<Banned />} />

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </div>
      </Router>
    </UserProvider>
  );
}

export default App;
